import { computedFrom } from 'aurelia-binding';
import { bindable, customElement } from 'aurelia-templating';
import { autoinject, transient } from "aurelia-dependency-injection";
import { Reflection } from '../../../utilities/reflection';

@autoinject()
@transient()
@customElement('hue-slider')
export class HueSlider {

    private offsetWidth: number = 0;
    public leftPosition: number = 0;
    private sliderPointer: HTMLElement;
    private container: HTMLElement;

    @bindable
    color: string = "000000";

    @bindable
    positionSlider: number = -1;

    constructor(private element: Element) { }

    positionSliderChanged(newValue: number) {
        const rgb = this.hslToRgb(newValue, 1, 0.5);
        this.color = this.rgb2hex(rgb[0], rgb[1], rgb[2]);
        const width = this.offsetWidth > 0 ? this.offsetWidth : this.container.offsetWidth;
        if ((this.offsetWidth == 0 || this.container.offsetWidth != this.offsetWidth) && this.container.offsetWidth > 0) {
            this.offsetWidth = this.container.offsetWidth;
        }
        this.leftPosition = newValue * width / 360 - this.sliderPointer.offsetWidth / 2;
        const paramsEvent: any = { detail: { value: newValue }, bubbles: true };
        const changePositionEvent: CustomEvent = new CustomEvent("position-changed", paramsEvent);
        this.element.dispatchEvent(changePositionEvent);
    }

    colorChanged(newValue: string) {
        const paramsEvent: any = { detail: { value: newValue }, bubbles: true };
        const changeColorEvent: CustomEvent = new CustomEvent("value-changed", paramsEvent);
        this.element.dispatchEvent(changeColorEvent);
    }

    bind() {
        if (!this.positionSlider || this.positionSlider < 0) {
            this.positionSlider = 0;
        }
    }
    @computedFrom(Reflection.getPropertyPath<HueSlider>(x => x.positionSlider))
    get getHslColor() {
        return 'hsl(' + this.positionSlider + ',100%,50%)';
    }

    attached() {
        let prevX = 0;
        const mouseMoveFunc = (e) => {
            if (e.pageX !== prevX) {
                prevX = e.pageX;
                const rect = this.container.getBoundingClientRect();
                const width = rect.width;
                let x = e.pageX - rect.left;
                x = x <= 0 ? 0 + 1 : (x >= width ? width - 1 : x);
                this.leftPosition = x - this.sliderPointer.offsetWidth / 2;
                this.positionSlider = 360 * ((x) / width);
            }
        };
        const mouseUpFunc = () => {
            document.removeEventListener('mousemove', mouseMoveFunc);
        };
        const mouseDownFunc = (e) => {
            const rect = this.container.getBoundingClientRect();
            const width = rect.width;
            let x = e.pageX - rect.left;
            x = x <= 0 ? 0 + 1 : (x >= width ? width - 1 : x);
            this.leftPosition = x - this.sliderPointer.offsetWidth / 2;
            this.positionSlider = 360 * ((x) / width);
            document.addEventListener('mousemove', mouseMoveFunc);
        };
        this.sliderPointer.addEventListener('mousedown', mouseDownFunc);
        this.container.addEventListener('mousedown', mouseDownFunc);
        document.addEventListener('mouseup', mouseUpFunc);
    }

    hslToRgb(hue, saturation, lightness) {
        // based on algorithm from http://en.wikipedia.org/wiki/HSL_and_HSV#Converting_to_RGB
        if (hue === undefined) {
            return [0, 0, 0];
        }

        const chroma = (1 - Math.abs((2 * lightness) - 1)) * saturation;
        let huePrime = hue / 60;
        const secondComponent = chroma * (1 - Math.abs((huePrime % 2) - 1));

        huePrime = Math.floor(huePrime);
        let red;
        let green;
        let blue;

        if (huePrime === 0) {
            red = chroma;
            green = secondComponent;
            blue = 0;
        } else if (huePrime === 1) {
            red = secondComponent;
            green = chroma;
            blue = 0;
        } else if (huePrime === 2) {
            red = 0;
            green = chroma;
            blue = secondComponent;
        } else if (huePrime === 3) {
            red = 0;
            green = secondComponent;
            blue = chroma;
        } else if (huePrime === 4) {
            red = secondComponent;
            green = 0;
            blue = chroma;
        } else if (huePrime === 5) {
            red = chroma;
            green = 0;
            blue = secondComponent;
        }

        const lightnessAdjustment = lightness - (chroma / 2);
        red += lightnessAdjustment;
        green += lightnessAdjustment;
        blue += lightnessAdjustment;

        return [Math.round(red * 255), Math.round(green * 255), Math.round(blue * 255)];

    }

    rgb2hex(red, green, blue) {
        const rgb = blue | (green << 8) | (red << 16);
        return (0x1000000 + rgb).toString(16).slice(1);
    }
}
